import React, { useState, useRef } from 'react';
import { useLogin, useNotify, useTranslate, useLocaleState } from 'react-admin';
import { Box, TextField, Button, Typography, Link, CircularProgress, Alert } from '@mui/material';
import { ElectricBolt } from '@mui/icons-material';
import { LanguageSwitcher } from './LanguageSwitcher';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { peekPostLoginRedirect, clearPostLoginRedirect } from '../utils/postLoginRedirect';
import { TurnstileWidget, TurnstileWidgetHandle } from './TurnstileWidget';
import { TURNSTILE_SITE_KEY } from '../config/api';

export const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [requires2FA, setRequires2FA] = useState(false);
    const [totpCode, setTotpCode] = useState('');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileWidgetHandle>(null);
    const turnstileEnabled = TURNSTILE_SITE_KEY !== '';
    const login = useLogin();
    const notify = useNotify();
    const translate = useTranslate();
    const [locale] = useLocaleState();
    const { settings: publicSettings } = usePublicSettings();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        try {
            // Include TOTP code if 2FA is required
            const credentials: any = requires2FA
                ? { username, password, totpCode }
                : { username, password };
            if (turnstileEnabled) {
                credentials.turnstileToken = turnstileToken;
            }

            const redirectTo = peekPostLoginRedirect();
            await login(credentials, redirectTo);
            clearPostLoginRedirect();
        } catch (error: any) {
            setLoading(false);

            // The Turnstile token is single-use and was consumed by this attempt.
            // Reset the widget so the next submit (the 2FA second step, or a retry
            // after a wrong password) gets a fresh token.
            if (turnstileEnabled) {
                setTurnstileToken(null);
                turnstileRef.current?.reset();
            }

            // Handle specific error codes
            if (error.errorCode === 'AUTH_2FA_REQUIRED' || error.requiresTwoFactor) {
                setRequires2FA(true);
                setError(null); // Clear any previous errors
                notify(translate('auth.twoFactorRequired'), { type: 'info' });
            } else if (error.errorCode === 'AUTH_INVALID_2FA') {
                const errorMessage = translate('errors.AUTH_INVALID_2FA');
                setError(errorMessage);
                setTotpCode(''); // Clear invalid code
                notify(errorMessage, { type: 'error' });
            } else if (error.errorCode) {
                // Translate error code to user-friendly message
                const errorMessage = translate(`errors.${error.errorCode}`, { _: error.message });
                setError(errorMessage);
                notify(errorMessage, { type: 'error' });
            } else if (error.message) {
                // Check if the message is a translation key (starts with 'errors.')
                const errorMessage = error.message.startsWith('errors.')
                    ? translate(error.message)
                    : error.message;
                setError(errorMessage);
                notify(errorMessage, { type: 'error' });
            } else {
                setError(translate('ra.auth.sign_in_error'));
                notify(translate('ra.auth.sign_in_error'), { type: 'error' });
            }
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                backgroundColor: '#F8F9FA',
            }}
        >
            {/* Left side - Login Form */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: { xs: 2, sm: 4 },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 420 }}>
                    {/* Language Switcher at top right of form */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                        <LanguageSwitcher />
                    </Box>
                    
                    {/* Logo and Title */}
                    <Box sx={{ mb: { xs: 3, sm: 5 }, textAlign: 'center' }}>
                        {/* Row 1: Larger Logo */}
                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                            {publicSettings.companyLogo ? (
                                <Box
                                    component="img"
                                    src={publicSettings.companyLogo}
                                    alt={publicSettings.companyName}
                                    sx={{
                                        width: { xs: 80, sm: 120 },
                                        height: { xs: 80, sm: 120 },
                                        borderRadius: 3,
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: { xs: 80, sm: 120 },
                                        height: { xs: 80, sm: 120 },
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, #009A75 0%, #00BF8F 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ElectricBolt sx={{ color: 'white', fontSize: { xs: 40, sm: 64 } }} />
                                </Box>
                            )}
                        </Box>
                        
                        {/* Row 2: Company Name Admin */}
                        <Typography 
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: '#1A1A1A',
                                letterSpacing: '-0.02em',
                                mb: { xs: 2, sm: 3 },
                                fontSize: { xs: '1.25rem', sm: '2.125rem' },
                            }}
                        >
                            {publicSettings.companyName} {translate('app.subtitle')}
                        </Typography>
                    </Box>

                    {/* Error Alert */}
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ mb: 3, borderRadius: 2 }}
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Login Form */}
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#1A1A1A' }}>
                                {translate('auth.emailOrUsername', { _: 'Email or Username' })}
                            </Typography>
                            <TextField
                                fullWidth
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder={translate('auth.enterEmail', { _: 'Enter your email' })}
                                required
                                autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        '& fieldset': {
                                            borderColor: '#E0E0E0',
                                            borderWidth: 1,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#009A75',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#009A75',
                                            borderWidth: 2,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1A1A1A' }}>
                                    {translate('ra.auth.password')}
                                </Typography>
                                <Link href="#" sx={{ fontSize: '0.875rem', color: '#009A75', textDecoration: 'none' }}>
                                    {translate('auth.forgotPassword')}
                                </Link>
                            </Box>
                            <TextField
                                fullWidth
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={translate('auth.enterPassword', { _: 'Enter your password' })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        '& fieldset': {
                                            borderColor: '#E0E0E0',
                                            borderWidth: 1,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#009A75',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#009A75',
                                            borderWidth: 2,
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* 2FA Code Input - Only shown when 2FA is required */}
                        {requires2FA && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#1A1A1A' }}>
                                    {translate('auth.twoFactorCode', { _: 'Two-Factor Authentication Code' })}
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder={translate('auth.enter6DigitCode', { _: 'Enter 6-digit code' })}
                                    required={requires2FA}
                                    autoFocus={requires2FA}
                                    inputProps={{ 
                                        maxLength: 6,
                                        pattern: '[0-9]{6}',
                                        inputMode: 'numeric',
                                        style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.25rem' }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            '& fieldset': {
                                                borderColor: '#E0E0E0',
                                                borderWidth: 1,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#009A75',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#009A75',
                                                borderWidth: 2,
                                            },
                                        },
                                    }}
                                />
                                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#606060' }}>
                                    {translate('auth.twoFactorHint', { _: 'Enter the code from your authenticator app' })}
                                </Typography>
                            </Box>
                        )}

                        {/* Cloudflare Turnstile - bot protection for the admin console */}
                        {turnstileEnabled && (
                            <TurnstileWidget
                                ref={turnstileRef}
                                siteKey={TURNSTILE_SITE_KEY}
                                onToken={setTurnstileToken}
                                language={locale ? locale.toLowerCase() : undefined}
                            />
                        )}

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            disabled={loading || (turnstileEnabled && !turnstileToken) || (requires2FA && !totpCode)}
                            sx={{
                                py: 1.75,
                                backgroundColor: '#009A75',
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                boxShadow: 'none',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#007F5F',
                                    boxShadow: '0 4px 12px rgba(0,154,117,0.4)',
                                },
                                '&:disabled': {
                                    backgroundColor: '#E0E0E0',
                                },
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : requires2FA ? (
                                translate('auth.verifyAndSignIn', { _: 'Verify & Sign In' })
                            ) : (
                                translate('ra.auth.sign_in')
                            )}
                        </Button>
                    </form>

                </Box>
            </Box>

            {/* Right side - Feature Showcase */}
            <Box
                sx={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #009A75 0%, #00BF8F 100%)',
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: '150%',
                        height: '150%',
                        background: 'radial-gradient(circle at 30% 107%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        transform: 'rotate(-45deg)',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, p: 6, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 3 }}>
                        {translate('auth.loginPageTitle', { _: 'Manage your EV charging network efficiently' })}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400, lineHeight: 1.6 }}>
                        {translate('auth.loginPageSubtitle', { _: 'Monitor charging stations, track usage analytics, manage users, and optimize your electric vehicle infrastructure all in one place.' })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};